<script setup lang="ts">
import { computed } from 'vue'
import { micromark } from 'micromark'
import { type Account, TheTooltip } from '@wastedondestiny/destiny-library'
import { Expansions } from '../enums/expansions'
import { releaseDetail } from '../releaseDetail'

const props = withDefaults(defineProps<{
  small?: boolean,
  expansion: number,
  account: Account,
  noInteract?: boolean,
  isDeluxe?: boolean
}>(), {
  small: false,
  noInteract: false,
  isDeluxe: false
})

const owns = computed(() => ((props.account.versionsOwned ?? 0) & props.expansion) === props.expansion)
const expansionDescription = computed(() => micromark(releaseDetail.get(props.expansion)?.shortDescription ?? ''))
const deluxeDescription = computed(() => props.expansion === Expansions.EdgeOfFate ? micromark(releaseDetail.get(Expansions.EdgeOfFate)?.deluxeDescription ?? '') : '')
</script>

<template>
  <div class="flex h-12 justify-center" :class="{ 'w-10 py-1': small, 'w-10': !small }">
    <TheTooltip v-if="$props.expansion === Expansions.Destiny2" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/destiny2.svg" alt="Destiny 2" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }"/>
      <template #icon>
        <img src="../assets/expansions/destiny2.svg" alt="Destiny 2" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.CurseOfOsiris" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/osiris.svg" alt="Curse of Osiris" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }"/>
      <template #icon>
        <img src="../assets/expansions/osiris.svg" alt="Curse of Osiris" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Warmind" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/warmind.svg" alt="Warmind" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }"/>
      <template #icon>
        <img src="../assets/expansions/warmind.svg" alt="Warmind" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Forsaken" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/forsaken.svg" alt="Forsaken" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }"/>
      <template #icon>
        <img src="../assets/expansions/forsaken.svg" alt="Forsaken" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.AnnualPass" :warning="!owns" :no-interact="noInteract">
      <div class="ml-2 mt-2 flex items-center h-8" :class="{ 'opacity-75': owns, 'opacity-25': !owns }">
        <img class="h-5 w-5 mx-0.5" src="../assets/expansions/forge.svg" alt="Season of the Forge" />
        <img class="h-5 w-5 mx-0.5" src="../assets/expansions/drifter.svg" alt="Season of the Drifter" />
        <img class="h-5 w-5 mx-0.5" src="../assets/expansions/opulence.svg" alt="Season of Opulence" />
      </div>
      <template #icon>
        <img src="../assets/expansions/annual_pass.svg" alt="Forsaken Annual Pass" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Shadowkeep" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/shadowkeep.svg" alt="Shadowkeep" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/shadowkeep.svg" alt="Shadowkeep" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.BeyondLight" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/beyond_light.svg" alt="Beyond Light" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/beyond_light.svg" alt="Beyond Light" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Anniversary30th" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/30thanniversary.png" alt="30th Anniversary" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/30thanniversary.png" alt="30th Anniversary" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.TheWitchQueen" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/witch_queen.svg" alt="The Witch Queen" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/witch_queen.svg" alt="The Witch Queen" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Lightfall" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/lightfall.svg" alt="Lightfall" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/lightfall.svg" alt="Lightfall" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion{{ isDeluxe ? ' (deluxe)' : '' }}</p>
        <div class="markdown" v-html="deluxeDescription" />
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.FinalShape" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/final_shape.svg" alt="Final Shape" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/final_shape.svg" alt="Final Shape" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion{{ isDeluxe ? ' (deluxe)' : '' }}</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.EdgeOfFate" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/edge_of_fate.png" alt="Edge of Fate" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/edge_of_fate.png" alt="Edge of Fate" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="deluxeDescription" />
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.expansion === Expansions.Renegades" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/renegades.png" alt="Renegades" class="mt-2" :class="{ 'h-6 w-6': small, 'h-8 w-8': !small, 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/renegades.png" alt="Renegades" class="h-5 w-5" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this expansion</p>
        <div class="markdown" v-html="expansionDescription" />
      </template>
    </TheTooltip>
  </div>
</template>